import { BackupData } from './dataBackupService';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface MigrationResult {
  success: boolean;
  version: string;
  changes: string[];
  errors: string[];
}

export class DataValidationService {
  private readonly CURRENT_VERSION = '1.0.0';
  private readonly SUPPORTED_VERSIONS = ['1.0.0'];

  validateBackupIntegrity(backupData: any): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    try {
      // Validate required fields
      if (!backupData.version) {
        errors.push('Missing version information');
      } else if (!this.SUPPORTED_VERSIONS.includes(backupData.version)) {
        warnings.push(`Version ${backupData.version} may not be fully supported. Current version: ${this.CURRENT_VERSION}`);
      }

      if (!backupData.timestamp) {
        errors.push('Missing timestamp');
      } else if (!this.isValidTimestamp(backupData.timestamp)) {
        errors.push('Invalid timestamp format');
      }

      if (!backupData.metadata) {
        errors.push('Missing metadata');
      } else {
        this.validateMetadata(backupData.metadata, errors, warnings);
      }

      // Validate data structure
      if (backupData.aspirations && !Array.isArray(backupData.aspirations)) {
        errors.push('Invalid aspirations data structure');
      }

      if (backupData.goals && !Array.isArray(backupData.goals)) {
        errors.push('Invalid goals data structure');
      }

      if (backupData.lists && !Array.isArray(backupData.lists)) {
        errors.push('Invalid lists data structure');
      }

      if (backupData.schedules && !Array.isArray(backupData.schedules)) {
        errors.push('Invalid schedules data structure');
      }

      // Validate data relationships
      this.validateDataRelationships(backupData, errors, warnings);

      // Validate individual record structures
      this.validateRecordStructures(backupData, errors, warnings);

      // Check for data consistency
      this.validateDataConsistency(backupData, errors, warnings);

    } catch (error) {
      errors.push(`Validation error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  migrateBackupData(backupData: any): MigrationResult {
    const changes: string[] = [];

    try {
      let migratedData = { ...backupData };

      // Handle version migrations
      if (!backupData.version || this.isVersionOlder(backupData.version, '1.0.0')) {
        const legacyResult = this.migrateFromLegacy(migratedData);
        migratedData = legacyResult.data;
        changes.push(...legacyResult.changes);
      }

      // Future version migrations would go here
      // if (this.isVersionOlder(migratedData.version, '1.1.0')) {
      //   const result = this.migrateTo110(migratedData);
      //   migratedData = result.data;
      //   changes.push(...result.changes);
      // }

      return {
        success: true,
        version: this.CURRENT_VERSION,
        changes,
        errors: []
      };

    } catch (error) {
      const errors = [`Migration error: ${error instanceof Error ? error.message : 'Unknown error'}`];
      return {
        success: false,
        version: backupData.version || 'unknown',
        changes,
        errors
      };
    }
  }

  sanitizeBackupData(backupData: BackupData): BackupData {
    const sanitized = { ...backupData };

    // Remove any sensitive data that shouldn't be in backups
    if (sanitized.aiConfig?.apiKey) {
      // Don't include full API key in backup for security
      sanitized.aiConfig = {
        ...sanitized.aiConfig,
        apiKey: '[CONFIGURED]' // Indicate it was configured but don't store the key
      };
    }

    // Sanitize user profile data
    if (sanitized.userProfile) {
      sanitized.userProfile = this.sanitizeUserProfile(sanitized.userProfile);
    }

    // Clean up any undefined or null values
    sanitized.aspirations = sanitized.aspirations || [];
    sanitized.goals = sanitized.goals || [];
    sanitized.lists = sanitized.lists || [];
    sanitized.schedules = sanitized.schedules || [];

    return sanitized;
  }

  repairBackupData(backupData: any): { data: BackupData; repairs: string[] } {
    const repairs: string[] = [];
    const repairedData = { ...backupData };

    // Repair missing required fields
    if (!repairedData.version) {
      repairedData.version = this.CURRENT_VERSION;
      repairs.push('Added missing version field');
    }

    if (!repairedData.timestamp) {
      repairedData.timestamp = new Date().toISOString();
      repairs.push('Added missing timestamp');
    }

    if (!repairedData.metadata) {
      repairedData.metadata = this.createDefaultMetadata();
      repairs.push('Added missing metadata');
    }

    // Repair array fields
    if (!Array.isArray(repairedData.aspirations)) {
      repairedData.aspirations = [];
      repairs.push('Repaired aspirations array');
    }

    if (!Array.isArray(repairedData.goals)) {
      repairedData.goals = [];
      repairs.push('Repaired goals array');
    }

    if (!Array.isArray(repairedData.lists)) {
      repairedData.lists = [];
      repairs.push('Repaired lists array');
    }

    if (!Array.isArray(repairedData.schedules)) {
      repairedData.schedules = [];
      repairs.push('Repaired schedules array');
    }

    // Repair data relationships
    this.repairDataRelationships(repairedData, repairs);

    // Repair individual records
    this.repairRecordStructures(repairedData, repairs);

    return {
      data: repairedData as BackupData,
      repairs
    };
  }

  // Private validation methods
  private validateMetadata(metadata: any, _errors: string[], warnings: string[]): void {
    if (!metadata.deviceInfo) {
      warnings.push('Missing device information');
    }

    if (!metadata.appVersion) {
      warnings.push('Missing app version');
    }

    if (!metadata.backupMethod) {
      warnings.push('Missing backup method');
    }
  }

  private validateDataRelationships(data: any, errors: string[], _warnings: string[]): void {
    // Validate that goals reference valid aspirations
    if (data.goals && data.aspirations) {
      const aspirationIds = new Set(data.aspirations.map((a: any) => a.id).filter(Boolean));

      data.goals.forEach((goal: any, index: number) => {
        if (goal.aspirationId && !aspirationIds.has(goal.aspirationId)) {
          errors.push(`Goal at index ${index} "${goal.title || 'Unknown'}" references non-existent aspiration: ${goal.aspirationId}`);
        }
      });
    }

    // Validate that lists reference valid goals
    if (data.lists && data.goals) {
      const goalIds = new Set(data.goals.map((g: any) => g.id).filter(Boolean));

      data.lists.forEach((list: any, index: number) => {
        if (list.goalId && !goalIds.has(list.goalId)) {
          errors.push(`List at index ${index} "${list.title || 'Unknown'}" references non-existent goal: ${list.goalId}`);
        }
      });
    }
  }

  private validateRecordStructures(data: any, errors: string[], warnings: string[]): void {
    // Validate aspirations
    if (data.aspirations) {
      data.aspirations.forEach((aspiration: any, index: number) => {
        if (!aspiration.id) {
          errors.push(`Aspiration at index ${index} is missing ID`);
        }
        if (!aspiration.title) {
          warnings.push(`Aspiration at index ${index} is missing title`);
        }
        if (!aspiration.category) {
          warnings.push(`Aspiration at index ${index} is missing category`);
        }
      });
    }

    // Validate goals
    if (data.goals) {
      data.goals.forEach((goal: any, index: number) => {
        if (!goal.id) {
          errors.push(`Goal at index ${index} is missing ID`);
        }
        if (!goal.title) {
          warnings.push(`Goal at index ${index} is missing title`);
        }
        if (!goal.aspirationId) {
          warnings.push(`Goal at index ${index} is not linked to an aspiration`);
        }
      });
    }

    // Validate lists
    if (data.lists) {
      data.lists.forEach((list: any, index: number) => {
        if (!list.id) {
          errors.push(`List at index ${index} is missing ID`);
        }
        if (!list.title) {
          warnings.push(`List at index ${index} is missing title`);
        }
        if (!Array.isArray(list.items)) {
          errors.push(`List at index ${index} has invalid items structure`);
        }
      });
    }
  }

  private validateDataConsistency(data: any, errors: string[], warnings: string[]): void {
    // Check for duplicate IDs
    const allIds = new Set();

    ['aspirations', 'goals', 'lists'].forEach(entityType => {
      if (data[entityType]) {
        data[entityType].forEach((entity: any, index: number) => {
          if (entity.id) {
            if (allIds.has(entity.id)) {
              errors.push(`Duplicate ID found: ${entity.id} in ${entityType} at index ${index}`);
            } else {
              allIds.add(entity.id);
            }
          }
        });
      }
    });

    // Check date consistency
    if (data.timestamp) {
      const backupDate = new Date(data.timestamp);
      const now = new Date();

      if (backupDate > now) {
        warnings.push('Backup timestamp is in the future');
      }

      const daysDiff = Math.abs(now.getTime() - backupDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysDiff > 365) {
        warnings.push(`Backup is over a year old (${Math.round(daysDiff)} days)`);
      }
    }
  }

  private migrateFromLegacy(data: any): { data: any; changes: string[] } {
    const changes: string[] = [];
    const migratedData = { ...data };

    // Add version if missing
    if (!migratedData.version) {
      migratedData.version = this.CURRENT_VERSION;
      changes.push('Added version field');
    }

    // Add metadata if missing
    if (!migratedData.metadata) {
      migratedData.metadata = this.createDefaultMetadata();
      changes.push('Added metadata structure');
    }

    // Migrate old date formats
    this.migrateDateFields(migratedData, changes);

    // Ensure required array fields exist
    ['aspirations', 'goals', 'lists', 'schedules'].forEach(field => {
      if (!migratedData[field]) {
        migratedData[field] = [];
        changes.push(`Added missing ${field} array`);
      }
    });

    return { data: migratedData, changes };
  }

  private migrateDateFields(data: any, changes: string[]): void {
    // Convert date strings to proper format if needed
    ['aspirations', 'goals', 'lists'].forEach(entityType => {
      if (data[entityType]) {
        data[entityType].forEach((entity: any) => {
          ['createdAt', 'updatedAt', 'deadline', 'completedAt'].forEach(dateField => {
            if (entity[dateField] && typeof entity[dateField] === 'string') {
              try {
                const date = new Date(entity[dateField]);
                if (isNaN(date.getTime())) {
                  delete entity[dateField];
                  changes.push(`Removed invalid ${dateField} from ${entityType}`);
                }
              } catch (error) {
                delete entity[dateField];
                changes.push(`Removed invalid ${dateField} from ${entityType}`);
              }
            }
          });
        });
      }
    });
  }

  private repairDataRelationships(data: any, repairs: string[]): void {
    // Remove invalid goal references from aspirations
    if (data.goals && data.aspirations) {
      const validAspirationIds = new Set(data.aspirations.map((a: any) => a.id).filter(Boolean));

      data.goals = data.goals.filter((goal: any) => {
        if (goal.aspirationId && !validAspirationIds.has(goal.aspirationId)) {
          repairs.push(`Removed goal "${goal.title}" with invalid aspiration reference`);
          return false;
        }
        return true;
      });
    }

    // Remove invalid goal references from lists
    if (data.lists && data.goals) {
      const validGoalIds = new Set(data.goals.map((g: any) => g.id).filter(Boolean));

      data.lists = data.lists.filter((list: any) => {
        if (list.goalId && !validGoalIds.has(list.goalId)) {
          repairs.push(`Removed list "${list.title}" with invalid goal reference`);
          return false;
        }
        return true;
      });
    }
  }

  private repairRecordStructures(data: any, repairs: string[]): void {
    // Repair aspirations
    if (data.aspirations) {
      data.aspirations = data.aspirations.filter((aspiration: any, index: number) => {
        if (!aspiration.id) {
          repairs.push(`Removed aspiration at index ${index} due to missing ID`);
          return false;
        }
        return true;
      });
    }

    // Repair goals
    if (data.goals) {
      data.goals = data.goals.filter((goal: any, index: number) => {
        if (!goal.id) {
          repairs.push(`Removed goal at index ${index} due to missing ID`);
          return false;
        }
        return true;
      });
    }

    // Repair lists
    if (data.lists) {
      data.lists = data.lists.filter((list: any, index: number) => {
        if (!list.id) {
          repairs.push(`Removed list at index ${index} due to missing ID`);
          return false;
        }

        // Ensure lists have valid items array
        if (!Array.isArray(list.items)) {
          list.items = [];
          repairs.push(`Repaired items array for list "${list.title}"`);
        }

        return true;
      });
    }
  }

  private sanitizeUserProfile(profile: any): any {
    const sanitized = { ...profile };

    // Remove or mask sensitive fields
    if (sanitized.email) {
      // Mask email for privacy
      const [local, domain] = sanitized.email.split('@');
      if (local && domain) {
        sanitized.email = `${local.charAt(0)}***@${domain}`;
      }
    }

    // Remove any potential PII fields
    delete sanitized.ssn;
    delete sanitized.creditCard;
    delete sanitized.password;

    return sanitized;
  }

  private createDefaultMetadata(): any {
    return {
      deviceInfo: 'Unknown Device',
      appVersion: this.CURRENT_VERSION,
      backupMethod: 'manual'
    };
  }

  private isValidTimestamp(timestamp: string): boolean {
    try {
      const date = new Date(timestamp);
      return !isNaN(date.getTime()) && date.toISOString() === timestamp;
    } catch {
      return false;
    }
  }

  private isVersionOlder(version1: string, version2: string): boolean {
    const v1Parts = version1.split('.').map(Number);
    const v2Parts = version2.split('.').map(Number);

    for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
      const v1Part = v1Parts[i] || 0;
      const v2Part = v2Parts[i] || 0;

      if (v1Part < v2Part) return true;
      if (v1Part > v2Part) return false;
    }

    return false;
  }
}

// Create singleton instance
export const dataValidationService = new DataValidationService();